import { logError } from '@/utils/logHandling';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Audio, InterruptionModeAndroid, InterruptionModeIOS } from 'expo-av';
import { INITIAL_STATE } from './constants';
import { AudioAction, AudioState } from './types';

export const reducer = (state: AudioState, action: AudioAction): AudioState => {
  const { type, payload } = action;

  switch (type) {
    case 'KEY_CHANGE': {
      return { ...state, ...payload };
    }
    case 'RESET': {
      return INITIAL_STATE;
    }
    default:
      return state;
  }
};

export const emptyAudioCache = async (): Promise<void> => {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const cacheKeys = keys.filter((eachValue) =>
      eachValue.includes('__audio__cache'),
    );
    await AsyncStorage.multiRemove(cacheKeys);
  } catch (error) {
    logError('Error while multi-removing cache keys from AsyncStorage', error);
  }
};

export const setAudioCache = async (
  key: string | null,
  value: string,
): Promise<void> => {
  try {
    if (!key) {
      return;
    }
    const allKeys = await AsyncStorage.getAllKeys();
    if (allKeys.includes(key)) {
      const [position, duration] = value.split('-');
      if (!Number(position) && !Number(!duration)) {
        return;
      }
      await AsyncStorage.setItem(key, value);
    } else {
      await AsyncStorage.setItem(key, value);
    }
  } catch (error) {
    logError('Error while setting audio cache:', error);
  }
};

const getAudioCache = async (key: string): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(key);
  } catch (error) {
    logError('Error while getting audio cache:', error);
    return null;
  }
};

export const setAudioModeSetup = async (): Promise<void> => {
  return Audio.setAudioModeAsync({
    staysActiveInBackground: true,
    interruptionModeAndroid: InterruptionModeAndroid.DoNotMix,
    shouldDuckAndroid: false,
    playThroughEarpieceAndroid: false,
    allowsRecordingIOS: false,
    interruptionModeIOS: InterruptionModeIOS.DoNotMix,
    playsInSilentModeIOS: true,
  });
};

export const trackedDataSetup = async (uniqueKey: string) => {
  const cachedData = await getAudioCache(uniqueKey);
  return cachedData ? cachedData.split('-') : null;
};

export const promisify = <T>(cb: Function, duration: number): Promise<T> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      cb(resolve);
    }, duration);
  });
};
